<script lang="ts" setup>
import type { Configuration } from '~/types/configurator'

const props = defineProps<{
  activePanel: string | null
  configuration: Configuration | null
  values: Record<string, Configuration['values'][number]>
  multiselectableAccessories: Record<string, Configuration['values']>
}>()

const emit = defineEmits<{
  loadConfiguration: []
  applyAccessoryChange: [
    option: Configuration['accessoriesOptions'][number],
    concreteOption: Configuration['accessoriesOptions'][number]['values'][number],
  ]
}>()

const userStore = useUser()
const { showPrices } = storeToRefs(userStore)

function isPicked(
  option: Configuration['accessoriesOptions'][number],
  concreteOption: Configuration['accessoriesOptions'][number]['values'][number]
) {
  // if it has children, check if one of them is picked
  if (concreteOption.childrenNids.length) {
    return concreteOption.childrenNids.some((childNid) => {
      const childOption = option.values.find(
        (val) => val.accessoryNid === childNid
      )
      return (
        childOption &&
        configuratorUtils.isAccessoryPicked(
          option,
          childOption,
          props.values,
          props.multiselectableAccessories
        )
      )
    })
  }
  return configuratorUtils.isAccessoryPicked(
    option,
    concreteOption,
    props.values,
    props.multiselectableAccessories
  )
}

function showAccessory(
  concreteOption: Configuration['accessoriesOptions'][number]['values'][number]
) {
  // don't show accessory if it has a parent and the accessory does not have price
  return !concreteOption.parentNid || concreteOption.price
}
</script>

<template>
  <template
    v-for="option in props.configuration?.accessoriesOptions"
    :key="option.partCategoryNid"
  >
    <div
      v-if="
        props.activePanel === ACCESSORIES_OPTIONS_PANEL + option.partCategoryNid
      "
      class="grid max-h-svh grid-cols-1 gap-1 overflow-y-auto @lg/main:grid-cols-2 @2xl/main:grid-cols-3"
    >
      <template
        v-for="concreteOption in option.values"
        :key="concreteOption.accessoryNid"
      >
        <div
          v-if="showAccessory(concreteOption)"
          class="group relative flex max-h-[15rem] min-h-[15rem] items-center justify-center bg-white hover:text-white @lg/main:aspect-square @lg/main:max-h-none @lg:min-h-[auto]"
        >
          <NuxtImg
            v-if="concreteOption.thumbnail"
            :src="withHost(concreteOption.thumbnail + IMG_SIZE_250_250_FIT)"
            class="z-0 col-start-1 row-start-1 mx-auto h-[55%] w-[calc(100%-1.5rem)] object-scale-down mix-blend-darken"
            alt=""
          />
          <Icon
            v-else-if="concreteOption.accessoryNid"
            class="z-0 text-xl text-black"
            :name="'fa6-solid:image'"
          />
          <SvgEmpty
            v-else
            class="z-20 h-[40%] stroke-neutral-200 stroke-[4] group-hover:stroke-neutral-500 group-hover:stroke-[3]"
            :class="{
              '!stroke-white !stroke-[3]': isPicked(option, concreteOption),
            }"
          />
          <SvgSelected
            v-if="
              concreteOption.accessoryNid && isPicked(option, concreteOption)
            "
            class="absolute z-20 h-[40%] stroke-white stroke-[3] group-hover:hidden"
          />
          <div
            class="absolute bottom-3 left-3 right-3 top-3 flex flex-col items-start justify-between bg-black bg-opacity-0 p-3 group-hover:bg-opacity-70"
            :class="{
              '!bg-opacity-70': isPicked(option, concreteOption),
            }"
          >
            <p
              class="font-pragmatica-ext text-lg font-bold text-black group-hover:text-white"
              :class="{
                '!text-white': isPicked(option, concreteOption),
                uppercase: concreteOption.accessoryNid,
              }"
            >
              {{ concreteOption.title }}
            </p>
            <div class="hidden w-full overflow-hidden group-hover:flex">
              <ul class="description-list w-full overflow-y-auto text-xs">
                <li
                  v-for="(item, index) in concreteOption.descriptionItems"
                  :key="index"
                >
                  {{ item }}
                </li>
              </ul>
            </div>
            <div class="w-full">
              <div class="flex w-full justify-start">
                <p
                  v-if="showPrices"
                  class="font-pragmatica-ext mt-1 text-sm font-bold"
                  :class="{
                    '!text-white': isPicked(option, concreteOption),
                    'mr-2': !concreteOption.isRemovable,
                  }"
                >
                  {{
                    configuratorUtils.getFormattedPrice(
                      concreteOption.price,
                      props.configuration?.currency,
                      true
                    )
                  }}
                </p>
                <UiTooltip
                  v-if="!concreteOption.isRemovable"
                  placement="bottom"
                  :label="
                    isPicked(option, concreteOption)
                      ? $t('configurator.nonRemovableAccessorySelected')
                      : $t('configurator.nonRemovableAccessoryHelp')
                  "
                  :offset-size="5"
                  strategy="absolute"
                  class="max-w-xs whitespace-normal break-normal rounded-none border-[1px] border-neutral-200 px-3 py-3 text-sm font-normal drop-shadow-sm"
                >
                  <Icon
                    class="text-black group-hover:text-white"
                    :name="'fa6-solid:circle-question'"
                    :class="{
                      '!text-white': isPicked(option, concreteOption),
                    }"
                  />
                </UiTooltip>
              </div>
              <button
                v-if="
                  concreteOption.isRemovable &&
                  (!isPicked(option, concreteOption) || option.multiselectable)
                "
                class="font-pragmatica-ext mt-1 hidden w-full border-2 border-white py-1 font-bold hover:bg-white hover:text-black group-hover:block"
                :class="{
                  'mt-2': !showPrices,
                }"
                @click="emit('applyAccessoryChange', option, concreteOption)"
              >
                {{
                  !isPicked(option, concreteOption)
                    ? $t('configurator.select')
                    : $t('configurator.deselect')
                }}
              </button>
            </div>
          </div>
        </div>
      </template>
    </div>
  </template>
</template>

<style lang="sass">
.description-list
  @apply list-disc pl-4
</style>
